import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { MovementTypes } from "constants/index";
import { useMemo } from "react";
import { RxArrowDown } from "react-icons/rx";
import { DetailTableProps } from "types";
import {
  CustomHeadCell,
  UnitHeaderCell,
} from "components/utils/LiquidationDetails";
import { arsFormatter } from "helpers";
import Row from "./MovementDetailRow";
import { getAbonoUnitValue, isAbonoSettlement, liquidationIncomeDischargeUnitsTotal } from "helpers/liquidations";

export default function DischargeDetailTable({
  liquidation,
  print = false,
  chip,
  hasDetail,
}: DetailTableProps) {
  const theme = useTheme();

  const settlementType = useMemo(
    () => liquidation.liquidation_settlement_types[2],
    [liquidation],
  );

  const movements = useMemo(() => {
    return liquidation.liquidation_movements
      .filter(
        (m) =>
          m.movement_type_id === MovementTypes.Discharge &&
          liquidation.liquidation_movement_articles.some(
            (ma) => ma.movement_id === m.movement_id,
          ),
      )
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [liquidation]);

  const isAbono = isAbonoSettlement(settlementType);

  const abonoUnitValue = getAbonoUnitValue(settlementType, movements.length);

  const showMinimumAdjustmentColumn = useMemo(() => {
    const minimumValue = Number(settlementType.minimum_value ?? 0);

    if (isAbono || minimumValue <= 0) return false;

    return movements.some((movement) => {
      const articles = liquidation.liquidation_movement_articles.filter(
        (ma) => ma.movement_id === movement.movement_id,
      );

      const unitsTotal = liquidationIncomeDischargeUnitsTotal(
        settlementType,
        movement,
        articles,
        liquidation,
      );

      return minimumValue > Number(unitsTotal ?? 0);
    });
  }, [isAbono, settlementType, movements, liquidation]);

  return (
    <Grid item xs={12}>
      <Accordion defaultExpanded={print}>
        <AccordionSummary
          expandIcon={
            !print && (
              <RxArrowDown style={{ color: theme.palette.primary.main }} />
            )
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Detalle de Picking y Despacho</Typography>
            {chip && (
              <Chip
                color={chip.color}
                size="small"
                label={chip.label}
                icon={chip.icon}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} elevation={4}>
            <Table size="small" aria-label="collapsible table">
              <TableHead
                sx={(theme) => ({
                  backgroundColor: theme.palette.primary.main,
                })}
              >
                <TableRow>
                  {hasDetail && <CustomHeadCell></CustomHeadCell>}
                  <CustomHeadCell>Tipo Mov.</CustomHeadCell>
                  <CustomHeadCell>Nº Mov</CustomHeadCell>
                  <CustomHeadCell>Doc. Cliente</CustomHeadCell>
                  <CustomHeadCell>Fecha</CustomHeadCell>
                  <CustomHeadCell>Cliente Asoc.</CustomHeadCell>
                  {UnitHeaderCell(settlementType.settlement_type)}
                  {showMinimumAdjustmentColumn && (
                    <CustomHeadCell>Cantidad Facturable</CustomHeadCell>
                  )}
                  <CustomHeadCell>Subtotal</CustomHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map((movement, index) => (
                  <Row
                    key={index}
                    row={movement}
                    liquidation={liquidation}
                    settlementType={settlementType}
                    print={print}
                    hasDetail={hasDetail}
                    isAbono={isAbono}
                    abonoUnitValue={abonoUnitValue}
                    showMinimumAdjustmentColumn={showMinimumAdjustmentColumn}
                  />
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={13} align="right">
                    <Typography variant="h6">
                      {"Total Picking y Despacho"}{" "}
                      {arsFormatter(settlementType.subtotal)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
}
